import { useCallback, useMemo } from 'react'
import { useUserProfile } from 'core/hooks/data/useDataHook'
import useCurrentTeam from 'core/hooks/team/useCurrentTeam'
import { StaticPurposes } from 'types/entity'
import { useSettings } from 'core/hooks/settings/useSettings'

interface Response {
  allPurposes: string[]
  teamPurposes: string[]
  customIncomeSources: string[]
  translatePurpose: (value: string) => {
    purpose: string
    income_source: string | null
  }
}

const usePurposes = (): Response => {
  const {
    data: {
      featureFlags: { ENABLE_DRIVER_EXPERIENCE: isMotus }
    }
  } = useSettings()
  const { data: user } = useUserProfile()
  const { data: team } = useCurrentTeam()
  const hasTeam = !!team?.token_id

  const customPurposes = useMemo(() => {
    return user?.custom_purposes ?? []
  }, [user?.custom_purposes])

  const customIncomeSources = useMemo(() => {
    return user?.income_sources ?? []
  }, [user?.income_sources])

  const teamPurposes = useMemo(() => {
    if (!hasTeam || !team?.purposes) return []
    return team.purposes.map((p) => p.name)
  }, [hasTeam, team?.purposes])

  const allPurposes = useMemo(() => {
    const combinedPurposes = [
      ...teamPurposes,
      'Work',
      'Personal',
      ...customIncomeSources,
      ...(isMotus ? [] : Object.values(StaticPurposes)),
      ...customPurposes
    ]
    if (hasTeam) {
      return [...teamPurposes, 'Personal']
    }
    return combinedPurposes
  }, [teamPurposes, customIncomeSources, customPurposes, hasTeam, isMotus])

  const translatePurpose = useCallback(
    (value: string) => {
      if (customIncomeSources.includes(value)) {
        return {
          purpose: 'Work',
          income_source: value
        }
      } else {
        return {
          purpose: value,
          income_source: null
        }
      }
    },
    [customIncomeSources]
  )

  return {
    allPurposes,
    teamPurposes,
    customIncomeSources,
    translatePurpose
  }
}

export default usePurposes
